import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import { Box, Container } from "@material-ui/core";
import { Loader } from "swyft-react-core";
import { useSelector, useDispatch } from "react-redux";
import { RootState, history } from "../../app/rootReducer";
import {
  fetchQuote,
  addAddonQuote,
  removeAddonQuote,
  changeQuantityAddonQuote,
  setInfoAddon,
  addSubscriptionQuote,
  removeSubscriptionQuote,
  releaseQuote
} from "../../store/QuoteSlice";
import { PageState } from "../../api/Types";
import { QuoteLineItem } from "../../api/Quote/QuoteLineItem";
import { useCookies } from "react-cookie";
import { CookieQuoteId, FeatureSchedule } from "../../app/consts";
import QuoteAddonWizardTop from "./components/QuoteAddonWizardTop";
import QuoteAddonWizardContent from "./components/QuoteAddonWizardContent";
import { allowCustomerEdits, quoteAvailableSubscriptions, quoteDetectSteps } from "../../app/utils";
import { AddonForm } from "../../api/Addon/AddonApi";
import { toast } from "react-toastify";
import QuoteAddonInfoDialog from "../quotePage/components/QuoteAddonInfoDialog";
import { useLocation } from "react-router-dom";
import QuoteRemoveDialog from "../quotePage/components/QuoteRemoveDialog";
import { fetchProductSubscriptions } from "../../store/ProductSubscriptionSlice";
import { ProductSubscription } from "../../api/Product/ProductSubscriptionApi";
import { mapProductSubscriptionToForm, QuoteSubscriptionForm } from "../../api/QuoteSubscription/QuoteSubscriptionApi";
import { EEventType, eventAnalytic } from "../../app/analytics";
import { EventSlot, EventSlotQueryParams } from "../../api/EventSlot/EventSlot";
import { useGetEventSlot, useQuoteEventSlots, useScheduleQuote } from "../../api/EventSlot/EventSlotApi";
import { applyPromoCode, removePromoCode } from "../../api/Quote/QuoteApi";
import { useQuoteSelectedEvent } from "../quoteCartPage/components/UseQuoteSelectedEvent";
import { QuoteAvailableDeduction } from "../quoteCartPage/components/QuoteAvailableDeduction";
import { useTranslation } from "react-i18next";
import OverrideTheme from "../../components/OverrideTheme";
import { useConfig } from "../../components/config/ConfigContext";

interface IState {
  lastStep?: boolean;
}

type Props = RouteComponentProps<{ id: string }>;
const QuoteAddonWizardPage: React.FC<Props> = ({ match }: Props) => {
  eventAnalytic.useEffect(EEventType.PageWizardAddonView);
  const { t } = useTranslation("ab");
  const dispatch = useDispatch();
  const [cookies, setCookie] = useCookies();
  const location = useLocation();
  const [lastStep, setLastStep] = React.useState((location.state as IState)?.lastStep);
  const [activeStep, setActiveStep] = React.useState(0);
  const [steps, setSteps] = React.useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);
  const [addonForRemove, setAddonForRemove] = useState<QuoteLineItem | null>(null);
  const [subscriptionForRemove, setSubscriptionForRemove] = useState<ProductSubscription | null>(null);
  const [selectedEventSlot, setSelectedEventSlot] = useState<EventSlot | null>(null); //date
  const { isFeatureEnabled } = useConfig();
  const quoteId = match.params.id;

  const [eventSlotQuote] = useState<EventSlotQueryParams>({
    quoteId: quoteId,
    page: 1,
    pageSize: 100
  });

  const { pageState, error, quote, infoAddon } = useSelector((state: RootState) => state.quote); //ok
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const { data: eventSlots } = useQuoteEventSlots(eventSlotQuote, isFeatureEnabled(FeatureSchedule)); //date

  const { data: selectedEventSlotInfo } = useGetEventSlot(quote?.eventSlotId); //date

  const scheduleQuote = useScheduleQuote(); //date

  const isAvailableSchedule = eventSlots!?.items?.length > 0 ?? false; //date

  useEffect(() => {
    dispatch(fetchProductSubscriptions());
    dispatch(fetchQuote(quoteId));
  }, [quoteId, dispatch]);

  useEffect(() => {
    if (quote) {
      setSteps(quoteDetectSteps(quote, subscriptions, isAvailableSchedule));
    }
  }, [quote, subscriptions, isAvailableSchedule]);

  useEffect(() => {
    if (selectedEventSlotInfo) {
      setSelectedEventSlot(selectedEventSlotInfo);
    }
  }, [selectedEventSlotInfo]); //date

  useQuoteSelectedEvent(
    quote,
    useCallback(() => {
      dispatch(releaseQuote(quoteId));
      setSelectedEventSlot(null);
    }, [dispatch, quoteId])
  ); //date

  useEffect(() => {
    if (lastStep && quote && steps.length > 1) {
      setActiveStep(steps.length - 1);
      setLastStep(false);
    }
  }, [lastStep, quote, steps, location, isAvailableSchedule]);

  if (!quote || !subscriptions) {
    return <Loader />;
  }

  if (pageState === PageState.Error || error) {
    return <div>Error</div>;
  }

  if (cookies[CookieQuoteId] !== quote.id) {
    setCookie(CookieQuoteId, quote.id, { path: "/" });
  }

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      history.push(`/quote/cart/${quote.id}`);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      history.push(`/quote/${quote.id}`);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onAddAddon = function (addon: QuoteLineItem) {
    if (addon.selected) {
      onRemoveAddon(addon);
      return;
    }
    eventAnalytic.record(addon);
    dispatch(
      addAddonQuote(quoteId, {
        addonId: addon.productId,
        quantity: addon.quantity
      } as AddonForm)
    );
    toast.success(addon.name + " has been added");
    onInfoDialogClose();
  };

  const onRemoveAddon = function (addon: QuoteLineItem) {
    setAddonForRemove(addon);
    onRemoveDialogOpen();
  };

  const onChangeQuantity = function (addon: QuoteLineItem, quantity: number) {
    dispatch(
      changeQuantityAddonQuote(quoteId, {
        addonId: addon.productId,
        quantity: quantity
      } as AddonForm)
    );
  };

  const onInfoDialogOpen = (addon: QuoteLineItem) => {
    eventAnalytic.record(EEventType.AddonViewDetails);
    setOpen(true);
    dispatch(setInfoAddon(addon));
  };

  const onInfoDialogClose = () => {
    setOpen(false);
  };

  const handleRemove = function (addon: QuoteLineItem) {
    dispatch(
      removeAddonQuote(quoteId, {
        addonId: addon.productId
      } as AddonForm)
    );
    eventAnalytic.record(EEventType.RemovedAddon);
    toast.success(addon.name + " has been removed");
  };

  const onRemoveDialogOpen = () => {
    setOpenRemove(true);
  };

  const onRemoveDialogClose = () => {
    setOpenRemove(false);
  };

  const onAddSubscription = (subscription: ProductSubscription) => {
    const selected = quote.subscriptions?.some((i) => i.productId === subscription.id);
    if (selected) {
      onRemoveSubscription(subscription);
      return;
    }
    eventAnalytic.record(subscription);
    dispatch(addSubscriptionQuote(quoteId, mapProductSubscriptionToForm(subscription)));
    toast.success(subscription.name + " has been added");
  };

  const onRemoveSubscription = function (subscription: ProductSubscription) {
    setSubscriptionForRemove(subscription);
    onRemoveDialogOpen();
  };

  const onLockEventSlot = async (eventSlot: EventSlot): Promise<boolean> => {
    try {
      if (quote.eventSlotId === eventSlot.id) {
        dispatch(releaseQuote(quoteId));
        setSelectedEventSlot(null);
        return false;
      }

      await scheduleQuote.mutateAsync({
        quoteId: quote.id,
        eventSlotId: eventSlot.id
      });
      dispatch(fetchQuote(quoteId));
    } catch (ex: any) {
      toast.error(ex);
      return false;
    }

    setSelectedEventSlot(eventSlot);
    return true;
  }; //date

  const handleRemoveSubscription = function (subscription: ProductSubscription) {
    dispatch(
      removeSubscriptionQuote(quoteId, {
        productId: subscription.id
      } as QuoteSubscriptionForm)
    );
    eventAnalytic.record(EEventType.RemovedSubscription);
    toast.success(subscription.name + " has been removed");
  };

  const btnNextText = (): string => {
    if (activeStep === steps.length - 1) {
      return t("quote.wizard.next.cart", "Select Payment Type");
    }

    return t("quote.wizard.next", "Next");
  };

  const availableSubscriptions = quoteAvailableSubscriptions(quote, subscriptions);

  const onApplyPromoCode = async (promoCode: string) => {
    await applyPromoCode(quote.id, promoCode);
    dispatch(fetchQuote(quoteId));
  };

  const onDeletePromoCode = async (quoteDeductionId: string) => {
    await removePromoCode(quote.id, quoteDeductionId);
    dispatch(fetchQuote(quoteId));
  };

  return (
    <Box>
      <OverrideTheme>
        <QuoteRemoveDialog
          open={openRemove}
          onClose={onRemoveDialogClose}
          onRemove={handleRemove}
          addon={addonForRemove}
          subscription={subscriptionForRemove}
          onRemoveSubscription={handleRemoveSubscription}
        />
        <QuoteAddonInfoDialog
          open={open}
          addon={infoAddon}
          onAdd={onAddAddon}
          onClose={onInfoDialogClose}
          onChangeQuantity={onChangeQuantity}
          allowEdits={allowCustomerEdits(quote)}
        />
        <QuoteAddonWizardTop
          quote={quote}
          steps={steps.length}
          activeStep={activeStep}
          onBack={handleBack}
          onNext={handleNext}
          btnNextText={btnNextText}
        />
        <QuoteAvailableDeduction quote={quote} />
      </OverrideTheme>
      <Container maxWidth="lg">
        <QuoteAddonWizardContent
          quote={quote}
          steps={steps}
          activeStep={activeStep}
          subscriptions={availableSubscriptions}
          onNext={handleNext}
          onAddAddon={onAddAddon}
          onInfoDialogOpen={onInfoDialogOpen}
          onChangeQuantity={onChangeQuantity}
          onRemoveAddon={onRemoveAddon}
          onAddSubscription={onAddSubscription}
          btnNextText={btnNextText}
          eventSlots={eventSlots?.items ?? null}
          onLockEventSlot={onLockEventSlot}
          selectedEventSlot={selectedEventSlot}
          onApplyPromoCode={onApplyPromoCode}
          onDeletePromoCode={onDeletePromoCode}
        />
      </Container>
    </Box>
  );
};

export default QuoteAddonWizardPage;
